// A transition SURFACE is an ordered GROUP of routes rendered as sliding panes. That single idea covers both
// things a surface has to do, and keeps them consistent with each other:
//
//   • a SWIPE steps one place (`swipeTargets`) — a finger drags one page in from the adjacent edge.
//   • a LINK to ANY member of the group slides straight there in ONE screen (`findLinkTransition`) — clicking
//     Report → Board animates as a single slide, not a flip through the tabs between them, and not an instant
//     swap with no animation because the two aren't neighbours.
//
// The DIRECTION of both falls out of the order: a member further down the list arrives from the right, one
// further up leaves to the right. So the array you render the tab bar from is the same array that decides
// which way everything travels — they cannot drift apart.
//
// A SURFACE RUNS IN ONE OF THE TWO KINDS (see `SurfaceKind`, and core/motion.config.ts for the vocabulary):
// LATERAL for peers, HIERARCHICAL for levels. The kind changes what the travel MEANS — and so what the
// direction is CALLED. On a lateral surface the two directions are simply NEXT and PREVIOUS: the pages sit
// side by side and nothing is stacked on anything. Only on a hierarchical surface are they PUSH and POP.
// Both share this one geometry, which is why `TransitionEdge` names the screen edge rather than the operation.
//
// This file is the SHARED, app-agnostic contract of the transition system — pure helpers, the outlet props,
// and the group model. The actual surface GROUPS (which routes, in which order, for whom) are app-specific
// and live OUTSIDE this file: static apps declare them as constants (see a `surfaces.ts`), parameterised apps
// build the concrete member paths at each mount site. That split is what lets this file stay byte-identical
// across the projects that share this engine, so an edit ports verbatim.
//
// `RoutePath` is a plain `string`: a group's members are the CONCRETE pathnames the router navigates to
// (`/teams/42/report`, `/admin/doctors`) — exactly what `router.navigate({ to })` and the live preview's
// memory history consume. Apps that want compile-time route-literal safety keep it where they DEFINE their
// groups (via `satisfies readonly FileRouteTypes['to'][]`), not here.

/** A concrete, navigable pathname (e.g. `/teams/42/report`). Runtime instances, not route identities. */
export type RoutePath = string

/**
 * Which of the two KINDS of route transition this surface performs — the industry's own axis (Apple's
 * flat/hierarchical, Material's lateral/forward-and-backward). See core/motion.config.ts.
 *
 * `'lateral'`      — the members are PEERS (a tab strip, the top-level section pager). Both panes travel at
 *                    full speed side by side, separated by a hairline seam: no depth parallax, no cast shadow.
 *                    Its directions are NEXT and PREVIOUS.
 * `'hierarchical'` — the members are LEVELS (the root push/pop surface). The arriving pane rides on top, the
 *                    one underneath trails at `TRANSITION_CONFIG.parallax` and dims, and the leading edge
 *                    casts a shadow. Its directions are PUSH and POP.
 */
export type SurfaceKind = 'lateral' | 'hierarchical'

/**
 * The phases a surface's transition passes through, in order: `idle` (no transition, no panes) → `dragging` (a
 * finger owns the panes) → `settling` (the release spring, or a link glide, is carrying them to an endpoint) →
 * `finishing` (landed; holding the covering pane until the router's commit reaches the live layer) → `idle`.
 *
 * Declared here rather than in the controller because it is now part of the contract: `incomingInteractive`
 * decides from it whether the incoming pane may be touched (see `interactiveIncoming`).
 */
export type TransitionPhase = 'idle' | 'dragging' | 'settling' | 'finishing'

/** Which screen edge the incoming route travels from — this fixes both the direction and the z-order. It is
 *  deliberately named for the EDGE, not the operation, because what the operation is called depends on the
 *  surface's kind (next/previous when lateral, push/pop when hierarchical). */
export type TransitionEdge = 'left' | 'right'

export interface RouteTransition {
  /** The group member being left. */
  from: RoutePath
  /** The group member being committed to. */
  to: RoutePath
  /**
   * `'right'` — the target enters from the right and rides ON TOP. Forward along the group: the NEXT member
   *              on a lateral surface, a PUSH on a hierarchical one. Dragged leftwards.
   * `'left'`  — the current view slides OFF to the right on top, revealing the target. Backward along the
   *              group: the PREVIOUS member when lateral, a POP when hierarchical.
   */
  edge: TransitionEdge
}

/**
 * The props every transition outlet takes — the ONE public contract, so "drop-in replacement, same props" is
 * compiler-enforced rather than a claim in each file's header.
 */
export interface TransitionOutletProps {
  /** The ORDERED group of routes this surface owns. A swipe steps one member; a link to ANY member slides
   *  straight there in one screen, in the direction their order implies (see findLinkTransition). */
  group: readonly RoutePath[]
  /**
   * The path of the route this outlet is mounted IN — required for a nested surface, omitted for the root
   * one. It travels to the live preview's shadow router so a layout hosting the surface renders only its
   * <Outlet/> into the pane (see route-preview.tsx), and it scopes the snapshot cache and the debug dump.
   */
  previewFrom?: RoutePath
  /** Start the gesture only from a screen-edge strip (iOS back-swipe style) instead of anywhere. A native
   *  scroll can't be limited to an edge strip, so the scroll engine ignores it — accepted for contract parity. */
  edgeOnly?: boolean
  /**
   * Which KIND of route transition this surface performs — see `SurfaceKind`.
   *
   * REQUIRED, deliberately. It began life as `flat?: boolean`, where an omitted prop has an obvious meaning; a
   * union does not, and the inherited default was never actually declared anywhere — it only emerged from
   * three separate `=== 'lateral'` comparisons, so an omitted `kind` silently picked the hierarchical branch
   * in the controller and CSS alike. Every mount site in both repos already states which kind it is.
   */
  kind: SurfaceKind
  /** Classes for the STAGE — the box the panes slide inside. This is the travelled area, so it should span
   *  the full width the routes occupy; a nested surface usually has to break out of its parent's padding. */
  className?: string
  /**
   * Classes for each PANE — the live route AND every preview alike, so all panes carry them. This is where a
   * route's padding belongs: padding left on a container OUTSIDE the stage doesn't travel with the page.
   */
  contentClassName?: string
  /**
   * Let the ARRIVING page be touched before the transition has finished — tap a button on it during the settle,
   * or start scrolling it — instead of it being a picture until the panes are dropped. Opt-in per surface, and
   * default OFF: the pane is a shadow router (see route-preview.tsx), so what interaction means there is a
   * policy question each surface answers for itself. `incomingInteractive` is the exact rule.
   *
   * BOTH engines honour it, but they can offer different amounts of it, and the difference is a platform one. A
   * native scroll owns every touch from the moment it claims the pan until it stops moving — so on the SCROLL
   * engine this cannot cover the visible slide at all, and unlocks only the window after the snap has LANDED and
   * before the router has committed (previously dead; see that file's header for all four windows). The GESTURE
   * engine drives the panes from pointer events it owns, so there the whole settle is interactive.
   */
  interactiveIncoming?: boolean
}

/**
 * May the incoming pane be touched right now?
 *
 * The question is NOT "is a transition running" but **"is a finger currently moving this pane?"** — because
 * that is the only state where a hit-test is dishonest: the pane is travelling under the finger, so whatever
 * was under it at pointer-down is not what is under it at pointer-up. Everything else is a page sitting still
 * (or coasting to a stop on a spring) with the user looking straight at it:
 *
 *   • `dragging` + moved     → NO. A finger is dragging the pane; a second-finger tap would mis-target, and the
 *                             `click` a drag would otherwise fire on whatever it started on is separately
 *                             swallowed (see use-edge-swipe).
 *   • `dragging` + not moved → YES. This is the CATCH: a finger put down on a settling transition stops it dead
 *                             and holds it. If that press turns out to be a tap, it must reach the page — this
 *                             is the case a phase-only rule gets wrong, because catching sends the controller
 *                             back to `dragging` before the tap's `click` is dispatched.
 *   • `settling`             → YES. The release spring, and every programmatic link glide (never finger-moved),
 *                             so a link's arriving page is live for the whole ~300ms it takes to land.
 *   • `finishing`            → YES. Landed, waiting on the router's commit — visually already the new page.
 *   • `idle`                 → NO. There is no pane.
 */
export function incomingInteractive(optedIn: boolean, phase: TransitionPhase, movedByFinger: boolean): boolean {
  if (!optedIn || phase === 'idle') return false
  return !(phase === 'dragging' && movedByFinger)
}

/** True when `pathname` is on `member` (exact or nested, e.g. `/teams/42/report/x` is on `/teams/42/report`). */
export function matchesFrom(pathname: string, member: string): boolean {
  return pathname === member || pathname.startsWith(member.endsWith('/') ? member : `${member}/`)
}

/**
 * Where `pathname` sits in the group, or -1. Matched by prefix, LONGEST first: a URL like `/teams/42/board`
 * must resolve to the Board member even if a shorter member is also a prefix. Exported for the scroll engine's
 * roster (it mounts the WHOLE group as slots, anchored on this index).
 */
export function indexOfMember(order: readonly RoutePath[], pathname: string): number {
  let best = -1
  let bestLength = -1
  for (let i = 0; i < order.length; i++) {
    const member = order[i]
    if (!member || !matchesFrom(pathname, member)) continue
    if (member.length > bestLength) {
      best = i
      bestLength = member.length
    }
  }
  return best
}

/** The one-step moves a SWIPE can make from `pathname` (0–2: one per edge). */
export function swipeTargets(order: readonly RoutePath[], pathname: string): RouteTransition[] {
  const i = indexOfMember(order, pathname)
  const here = order[i]
  if (i < 0 || !here) return []
  const out: RouteTransition[] = []
  const next = order[i + 1]
  const previous = order[i - 1]
  if (next) out.push({ from: here, to: next, edge: 'right' }) // drag left → forward, to the next member
  if (previous) out.push({ from: here, to: previous, edge: 'left' }) // drag right → back, to the previous one
  return out
}

/**
 * The slide to play for a link/back-forward from `from` to `to` — for ANY two distinct members, adjacent or
 * not. The direction comes from their order, so a jump across the group is one slide the correct way, and a
 * navigation that leaves the group (or stays put) returns undefined and is left alone.
 */
export function findLinkTransition(order: readonly RoutePath[], from: string, to: string): RouteTransition | undefined {
  const i = indexOfMember(order, from)
  const j = indexOfMember(order, to)
  const source = order[i]
  const target = order[j]
  if (i < 0 || j < 0 || i === j || !source || !target) return undefined
  return { from: source, to: target, edge: j > i ? 'right' : 'left' }
}

/**
 * The path a group's nav chrome (tab bar) should highlight as active DURING a slide. The transition stores a
 * single global `override` (navVisualPath) that flips at the animation midpoint — but that override belongs to
 * whichever surface is sliding, and surfaces NEST. Honouring an override that targets no member of THIS group
 * dims every tab. So take the override only when it lands on one of `order`'s members; otherwise rest on the
 * committed `realPath`, keeping the tab you're leaving lit until the strip slides away.
 */
export function groupVisualPath(order: readonly string[], override: string | null, realPath: string): string {
  if (override !== null && order.some((member) => matchesFrom(override, member))) return override
  return realPath
}

/** Where the sliding underline should sit this frame: travelling between two members of its own group, or
 *  resting at one member. */
export type MarkerPlacement =
  | { mode: 'travel'; from: string; to: string; progress: number }
  | { mode: 'rest'; key: string }

/**
 * Decide the sliding underline's target for the current frame. It travels only when the running slide's BOTH
 * endpoints are members of this strip (`inGroup`); a slide belonging to another surface has nothing here to
 * travel between, so the marker rests under the committed `activePath` and rides away with it. No slide → rest.
 */
export function markerPlacement(
  slide: { from: string; to: string; progress: number } | null,
  inGroup: (member: string) => boolean,
  activePath: string,
): MarkerPlacement {
  if (slide && inGroup(slide.from) && inGroup(slide.to)) {
    const progress = slide.progress < 0 ? 0 : slide.progress > 1 ? 1 : slide.progress
    return { mode: 'travel', from: slide.from, to: slide.to, progress }
  }
  return { mode: 'rest', key: activePath }
}
